import { useMemo, useState } from 'react';
import { Crown, RotateCcw, ShieldCheck, ShieldX, Users } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreEffectivePermission, StoreMember } from '@/client/types/stores/parity';
import { useStoreMemberPermissionsQuery, useStoreMembersQuery } from '@/client/api/stores/parity.query';
import { useApplyStorePreset, useRemoveStoreOverride } from '@/client/api/stores/parity.mutation';
import { useSetStoreStaffPermissions } from '@/client/api/stores/stores.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { cn } from '@/lib/utils';

import { Pill, SectionCard, label, CardSkeleton, ListSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';

const PRESETS: { ourid: 2 | 3 | 4; label: string }[] = [
  { ourid: 2, label: 'Admin' },
  { ourid: 3, label: 'Manager' },
  { ourid: 4, label: 'Viewer' },
];

const ROLE_TONE: Record<string, string> = {
  owner: 'bg-amber-100 text-amber-800',
  administrator: 'bg-purple-100 text-purple-800',
  admin: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  viewer: 'bg-gray-100 text-gray-600',
  voucher_manager: 'bg-teal-100 text-teal-800',
};

type PendingChange =
  | { kind: 'set'; code: string; name: string; granted: boolean }
  | { kind: 'clear'; code: string; name: string }
  | { kind: 'preset'; ourid: 2 | 3 | 4; name: string };

const displayName = (m: StoreMember) => m.name || (m.username ? `@${m.username}` : `User #${m.uid}`);

const MemberPermissions: React.FC<{ storeid: number; member: StoreMember }> = ({ storeid, member }) => {
  const { data, isLoading } = useStoreMemberPermissionsQuery(storeid, member.uid);
  const setPerms = useSetStoreStaffPermissions();
  const clear = useRemoveStoreOverride();
  const preset = useApplyStorePreset();
  const canEdit = useGate(PERMISSIONS.store.permissions).allowed;
  const [pending, setPending] = useState<PendingChange | null>(null);
  const isOwner = member.ourid === 1;

  const byCategory = useMemo(() => {
    const out = new Map<string, StoreEffectivePermission[]>();
    for (const p of data?.permissions ?? []) {
      const k = p.category || 'Other';
      out.set(k, [...(out.get(k) ?? []), p]);
    }
    return out;
  }, [data]);

  if (isLoading || !data) return <ListSkeleton rows={6} />;

  const run = (reason: string) => {
    if (!pending) return;
    const done = { onSuccess: () => setPending(null) };
    if (pending.kind === 'set')
      setPerms.mutate({ storeid, uid: member.uid, permissions: [pending.code], granted: pending.granted, reason }, done);
    else if (pending.kind === 'clear') clear.mutate({ storeid, uid: member.uid, code: pending.code, reason }, done);
    else preset.mutate({ storeid, uid: member.uid, preset_ourid: pending.ourid, reason }, done);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{data.granted.length}</span> of {data.permissions.length} permissions granted
          {member.override_count ? ` · ${member.override_count} override${member.override_count === 1 ? '' : 's'}` : ''}
        </p>
        {!isOwner && (
          <div className="flex items-center gap-1">
            <span className="mr-1 text-xs text-gray-500">Reset to preset:</span>
            {PRESETS.map((p) => (
              <PermissionButton key={p.ourid} code={PERMISSIONS.store.permissions} size="sm" variant="outline"
                onClick={() => setPending({ kind: 'preset', ourid: p.ourid, name: p.label })}>
                {p.label}
              </PermissionButton>
            ))}
          </div>
        )}
      </div>

      {isOwner && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">The owner has every permission; it can't be overridden.</p>}

      <div className="grid gap-3 md:grid-cols-2">
        {Array.from(byCategory.entries()).map(([category, perms]) => (
          <div key={category} className="rounded-lg border border-gray-100 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{category}</p>
            <div className="space-y-1.5">
              {perms.map((p) => (
                <div key={p.code} className="flex items-center gap-2 text-sm">
                  {p.granted ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldX className="h-4 w-4 text-gray-300" />}
                  <span className={cn('min-w-0 flex-1 truncate', !p.granted && 'text-gray-500')} title={p.code}>{p.name ?? p.code}</span>
                  {p.source === 'override' && <Pill tone="bg-purple-50 text-purple-700 normal-case">override</Pill>}
                  {!isOwner && canEdit && (
                    <span className="flex gap-1">
                      <button type="button" onClick={() => setPending({ kind: 'set', code: p.code, name: p.name ?? p.code, granted: !p.granted })}
                        className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50">
                        {p.granted ? 'Deny' : 'Grant'}
                      </button>
                      {p.source === 'override' && (
                        <button type="button" onClick={() => setPending({ kind: 'clear', code: p.code, name: p.name ?? p.code })}
                          className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Clear override" title="Back to role default">
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {pending && (
        <ReasonDialog
          open
          title={
            pending.kind === 'preset'
              ? `Reset ${displayName(member)} to the ${pending.name} preset`
              : pending.kind === 'clear'
                ? `Clear override: ${pending.name}`
                : `${pending.granted ? 'Grant' : 'Deny'} “${pending.name}”`
          }
          description={
            pending.kind === 'preset'
              ? 'Replaces all of this member’s overrides with the preset’s permission set.'
              : pending.kind === 'clear'
                ? 'The member goes back to their role default for this permission.'
                : 'An explicit override for this store, which wins over the member’s role.'
          }
          confirmLabel="Apply"
          destructive={pending.kind === 'set' && !pending.granted}
          reasonRequired
          loading={setPerms.isPending || clear.isPending || preset.isPending}
          onConfirm={run}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  );
};

/**
 * Everyone with access to the store (`GET /<id>/members`) and each member's
 * effective permissions, with per-code grant/deny/clear and role presets
 * (`admin.store.permissions`) — the seller's Team page, from the console.
 */
export const StaffTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data, isLoading } = useStoreMembersQuery(store.storeid);
  const [selected, setSelected] = useState<number | null>(null);
  const members = data?.users ?? [];
  const current = members.find((m) => m.uid === selected) ?? null;

  if (isLoading)
    return (
      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <CardSkeleton description><ListSkeleton rows={4} media="avatar" trailing={false} /></CardSkeleton>
        <CardSkeleton description><ListSkeleton rows={6} /></CardSkeleton>
      </div>
    );

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <SectionCard title="Members" description={store.oid != null ? 'Owner and organization members' : 'Personal store — owner only'}>
        {members.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-400">No members.</p>
        ) : (
          <div className="-mx-2 divide-y divide-gray-100">
            {members.map((m) => (
              <button
                key={m.uid}
                type="button"
                onClick={() => setSelected(m.uid)}
                className={cn('flex w-full items-center gap-3 px-2 py-2.5 text-left hover:bg-gray-50', selected === m.uid && 'bg-teal-50')}
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                  {m.ourid === 1 ? <Crown className="h-4 w-4 text-amber-500" /> : displayName(m).replace('@', '').charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{displayName(m)}</span>
                  <span className="block truncate text-xs text-gray-500">{m.email ?? `uid ${m.uid}`}</span>
                </span>
                <Pill tone={ROLE_TONE[m.org_role?.toLowerCase()] ?? 'bg-gray-100 text-gray-600'}>{label(m.org_role)}</Pill>
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title={current ? `${displayName(current)} · permissions` : 'Permissions'}
        description={current ? 'Role defaults merged with this store’s overrides.' : undefined}
      >
        {current ? (
          <MemberPermissions key={current.uid} storeid={store.storeid} member={current} />
        ) : (
          <p className="flex items-center gap-2 text-sm text-gray-400"><Users className="h-4 w-4" /> Pick a team member.</p>
        )}
      </SectionCard>
    </div>
  );
};
